import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import '../CompanyHistory.css';
import { db } from '../../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

// E-Procurement Center Component
const EProcurementCenter = () => {
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const postsPerPage = 10;

  // Load posts from Firebase in real-time
  useEffect(() => {
    const postsRef = collection(db, 'en-e-procurement');
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
    });

    // Unsubscribe when component unmounts
    return () => unsubscribe();
  }, []);

  // Search filtering
  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  // PDF download
  const handleDownload = (post) => {
    if (post.pdfUrl) {
      window.open(post.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (post.pdfFile) {
      const link = document.createElement('a');
      link.href = post.pdfFile;
      link.download = post.pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Post click - password verification or popup display
  const handlePostClick = async (post) => {
    if (post.password && post.password.trim() !== '') {
      setSelectedPost(post);
      setShowPasswordModal(true);
      setPasswordInput('');
    } else {
      setSelectedPost(post);
      setShowPopup(true);
      
      // Increase view count
      try {
        const { doc, updateDoc, increment } = await import('firebase/firestore');
        const postRef = doc(db, 'en-e-procurement', post.id);
        await updateDoc(postRef, {
          viewCount: increment(1)
        });
      } catch (error) {
        console.error('Failed to increase view count:', error);
      }
    }
  };

  // Password verification
  const handlePasswordSubmit = async () => {
    if (passwordInput === selectedPost.password) {
      setShowPasswordModal(false);
      setShowPopup(true);
      
      // Increase view count
      try {
        const { doc, updateDoc, increment } = await import('firebase/firestore');
        const postRef = doc(db, 'en-e-procurement', selectedPost.id);
        await updateDoc(postRef, {
          viewCount: increment(1)
        });
      } catch (error) {
        console.error('Failed to increase view count:', error);
      }
    } else {
      alert('Incorrect password.');
    }
  };

  // Close popup
  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedPost(null);
  };

  // Close password modal
  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedPost(null);
    setPasswordInput('');
  };

  // Page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="board-container">
      {/* Board Header */}
      <div className="board-header">
        <h2>🛒 E-Procurement</h2>
        <p>Please fill out the form below and contact us via email, fax, or mail. (Fax 032-819-0518 Phone 032-811-0511)</p>
      </div>

      {/* Board Content */}
      <div className="board-content">
        <div className="board-table">
          <table>
            <thead>
              <tr>
                <th className="col-number">No.</th>
                <th className="col-title">Title</th>
                <th className="col-author">Author</th>
                <th className="col-date">Date</th>
                <th className="col-views">Views</th>
              </tr>
            </thead>
            <tbody>
              {currentPosts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-posts">No posts available.</td>
                </tr>
              ) : (
                currentPosts.map((post, index) => (
                  <tr key={post.id} onClick={() => handlePostClick(post)} className="post-row">
                    <td className="col-number">
                      {filteredPosts.length - startIndex - index}
                    </td>
                    <td className="col-title">
                      {post.title}
                      {post.password && post.password.trim() !== '' && (
                        <span className="password-icon">🔒</span>
                      )}
                    </td>
                    <td className="col-author">Admin</td>
                    <td className="col-date">
                      {post.createdAt ? 
                        new Date(post.createdAt.seconds ? post.createdAt.seconds * 1000 : post.createdAt).toLocaleDateString('en-US') : 
                        'No date'
                      }
                    </td>
                    <td className="col-views">
                      {post.viewCount || 0}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Search */}
        <div className="search-section">
          <input
            type="text"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="page-btn"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`page-btn ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            ))}
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="page-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Password Modal */}
      {showPasswordModal && selectedPost && (
        <div className="modal-overlay">
          <div className="password-modal">
            <h3>Password Required</h3>
            <p>This post is password protected. Please enter the password:</p>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Enter password"
              className="password-input"
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
            />
            <div className="modal-actions">
              <button onClick={handleClosePasswordModal} className="btn-cancel">
                Cancel
              </button>
              <button onClick={handlePasswordSubmit} className="btn-submit">
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post Detail Popup */}
      {showPopup && selectedPost && (
        <div className="modal-overlay">
          <div className="post-popup">
            <div className="popup-header">
              <h3>{selectedPost.title}</h3>
              <button onClick={handleClosePopup} className="close-btn">×</button>
            </div>
            <div className="popup-content">
              <div className="post-meta">
                <span>Date: {
                  selectedPost.createdAt ? 
                    new Date(selectedPost.createdAt.seconds ? selectedPost.createdAt.seconds * 1000 : selectedPost.createdAt).toLocaleDateString('en-US') : 
                    'No date'
                }</span>
                <span>Views: {selectedPost.viewCount || 0}</span>
              </div>
              <div className="post-content">
                <p>{selectedPost.content}</p>
              </div>
              {selectedPost.pdfFileName && (
                <div className="download-section">
                  <p>📄 {selectedPost.pdfFileName}</p>
                  <button 
                    onClick={() => handleDownload(selectedPost)}
                    className="download-btn"
                  >
                    Download
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EProcurement = () => {
  const [activeSection, setActiveSection] = useState('e-procurement-center');
  
  const sidebarItems = [
    { name: 'E-Procurement', path: 'e-procurement-center' }
  ];

  // Scroll spy functionality
  useEffect(() => {
    const handleScroll = () => {
      const sections = sidebarItems.map(item => item.path);
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sidebarItems]);

  // Hash URL handling (move to corresponding section on page load)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && sidebarItems.some(item => item.path === hash)) {
      setActiveSection(hash);
      setTimeout(() => {
        scrollToSection(hash);
      }, 100);
    }
  }, [sidebarItems]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 100; // 헤더 높이
      const elementPosition = element.offsetTop - headerHeight - 30; // 헤더 높이 + 여백
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToSection(sectionId);
  };

  return (
    <>
      <Sidebar 
        category="E-Procurement" 
        items={sidebarItems}
        activeSection={activeSection}
        onItemClick={handleSectionChange}
      />
      <div className="company-history-page">
        <div className="history-container">
          <h1 className="page-category-title">E-Procurement</h1>
          
          {/* E-Procurement Section */}
          <section id="e-procurement-center" className="section">
            <h2 className="section-title">E-Procurement</h2>
            <div className="section-container">
              <EProcurementCenter />
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default EProcurement;
